import type {Call} from '@bcio-voice/contracts';

/** A reported recorder observation is not a confirmation of remote privacy. */
export function recorderObservation(state:Call['recording_state']):string{
 const labels:Record<Call['recording_state'],string>={off:'Off',awaiting_consent:'Waiting for consent',recording:'Active',paused:'Paused',processing:'Processing audio',ready:'Saved audio',failed:'Failed'};
 return `${labels[state]}. This recorder observation does not confirm the capture privacy barrier.`;
}

/** Transcripts only (Allen, 2026-10-29): consented capture feeds live transcription and no call audio is recorded, so the
 * recorder reports 'off' while transcription runs or has run. A plain state, never a failure. */
export const TRANSCRIPTS_ONLY_TEXT='Not recorded (transcripts only)';
/** The call was transcribed with no recording: recorder off, transcription started (live, pending, delayed or complete). */
export function transcriptsOnly(call:{recording_state:Call['recording_state'];transcription_state?:Call['transcription_state']|null|undefined}):boolean{
 return call.recording_state==='off'&&(call.transcription_state==='pending'||call.transcription_state==='live'||call.transcription_state==='delayed'||call.transcription_state==='complete');
}

/** Outcomes where nobody picked up, so there was never a conversation to transcribe. */
const UNANSWERED_OUTCOMES=new Set<string>(['missed','no_answer','busy','rejected','cancelled','failed','blocked']);

/** Whether the call reached a conversation: false once it ended unanswered (no answer time, or a busy, no-answer,
 * cancelled, missed, declined, failed or blocked outcome), true when it was answered, and undefined while it is
 * still being set up and may yet be answered. */
export function callAnswered(call:{state?:Call['state'];answered_at?:string|null;outcome?:string|null}):boolean|undefined{
 if(call.outcome&&UNANSWERED_OUTCOMES.has(call.outcome))return false;
 if(call.answered_at)return true;
 if(call.state==='active'||call.state==='held'||call.state==='dropping')return true;
 if(call.state==='created'||call.state==='ringing')return undefined;
 return false;
}

/** What the transcription label knows about the call beyond its state. Leave a field undefined when it is unknown. */
export interface TranscriptContext{
 /** From callAnswered(): false means the call ended without a conversation. */
 answered?:boolean|undefined;
 /** Transcript segments the call has, once the transcript is loaded. */
 segments?:number|undefined;
 /** The call has ended (or failed). Finished calls say "Transcript ready" or "Transcript processing", never "Live". */
 finished?:boolean|undefined;
}

/** Transcription is independent of recorder activity. A finished call almost always ends 'delayed' (complete needs
 * every sample of every stream covered, and silence is uncovered; media-lifecycle.ts), so delayed is the normal terminal
 * label: neutral "automatic, may have gaps", "Transcript ready" once a finished call has lines, never a failure or a
 * catch-up promise. Only 'failed' is a failure. 'off' on a call nobody answered means there was nothing to transcribe. */
export function transcriptStateText(state:Call['transcription_state']|undefined,configured:boolean,context:TranscriptContext={}):{text:string;tone:'neutral'|'live'|'warn'}{
 switch(state){
  case 'off':
   if(context.answered===false)return {text:'No conversation to transcribe',tone:'neutral'};
   return {text:configured?'Off for this call (policy)':'Not set up',tone:'neutral'};
  // A call that just ended can still read live or pending until the last lines are written: still arriving, not live.
  case 'pending':return context.finished?{text:'Transcript processing',tone:'neutral'}:{text:'Starting…',tone:'neutral'};
  case 'live':return context.finished?{text:'Transcript processing',tone:'neutral'}:{text:'Live',tone:'live'};
  case 'delayed':
   if(context.segments===undefined)return {text:'Automatic; a few gaps possible',tone:'neutral'};
   if(context.segments>0)return {text:context.finished?'Transcript ready (automatic; a few gaps possible)':'Transcript (automatic; a few gaps possible)',tone:'neutral'};
   return {text:'No transcript captured',tone:'neutral'};
  case 'complete':return {text:context.finished?'Transcript ready':'Transcript',tone:'neutral'};
  case 'failed':return {text:'Transcript failed. Recording status is shown separately. Automatic catch-up is not available.',tone:'warn'};
  default:return {text:'Not confirmed',tone:'neutral'};
 }
}

/** The call aggregate never grants playback access or proves the archive adapter
 * is reachable now. Actual playable segments are shown in the recording panel. */
export function captureArchiveText(state:Call['recording_state']):string{
 switch(state){
  case 'ready':return 'Saved. Playback availability and access are checked in Recordings.';
  case 'processing':return 'Processing. Archive playback is not confirmed yet.';
  case 'failed':return 'A complete archive is not confirmed. Check Recordings for any saved segments.';
  default:return 'Playback availability and access are checked separately in Recordings.';
 }
}
